<template>
    <div class="card mb-4 card-collapsible">
        <a v-b-toggle.show-filters href="#" class="card-header">
            <h2 class="card-title">
                {{ $t('cars.search.filter.panelTitle') }}
                <i class="fa fa-chevron-down pull-right"></i>
            </h2>
        </a>
        <b-collapse id="show-filters" class="collapse" @shown="refreshSlider">
            <table class="table mb-0">
                <tr v-for="(options, filter) in availableFilters" v-if="Object.size(options) > 1" :key="filter">
                    <th class="bg-light td-shrink">{{ $t('cars.search.filter.label.' + filter) }}</th>
                    <td class="pb-1">
                        <div class="row gutter-xs">
                            <div v-for="(value, key) in options" class="col-sm-2" :key="filter + key">
                                <label :for="filter + key" class="btn btn-sm btn-light text-left w-100 text-truncate">
                                    <input type="checkbox" :id="filter + key" v-model="filterModel[filter][key]">
                                    <template v-if="typeof value == 'object'">
                                        <template v-if="value.type == 'image'">
                                            <img :src="value.src" :alt="value.name">
                                        </template>
                                    </template>
                                    <template v-else>
                                        {{ value }}
                                    </template>
                                </label>
                            </div>
                        </div>
                    </td>
                </tr>
                <tr>
                    <th class="bg-light td-shrink">{{ $t('cars.search.filter.label.price') }}</th>
                    <td>
                        <div class="row no-gutters">
                            <div class="col-2">
                                <div class="input-group">
                                    <div class="input-group-prepend">
                                        <div class="input-group-text">$</div>
                                    </div>
                                    <input type="number"
                                           step="0.01"
                                           :min="lowestPrice"
                                           class="form-control"
                                           v-model="minPrice"
                                           @blur="minPrice = minPrice < lowestPrice ? lowestPrice : minPrice">
                                </div>
                            </div>
                            <div class="col-8">
                                <div class="pt-1 pl-2 pr-2">
                                    <vue-slider ref="priceSlider"
                                                :value="priceSliderValue"
                                                :tooltip="false"
                                                :height="12"
                                                :circle-height="24"
                                                :min="lowestPrice"
                                                :max="highestPrice"
                                                @callback="handlePriceChange">
                                    </vue-slider>
                                </div>
                            </div>
                            <div class="col-2">
                                <div class="input-group">
                                    <div class="input-group-prepend">
                                        <div class="input-group-text">$</div>
                                    </div>
                                    <input type="number"
                                           :max="highestPrice"
                                           step="0.01"
                                           class="form-control"
                                           v-model="maxPrice"
                                           @blur="maxPrice = maxPrice > highestPrice ? highestPrice : maxPrice">
                                </div>
                            </div>
                        </div>

                    </td>
                </tr>
            </table>
            <div class="card-footer text-center">
                <button class="btn btn-sm btn-primary button-update-all"
                        @click="applyFilters">
                    <i class="fa fa-fw fa-search"></i>&nbsp; {{ $t('cars.search.filter.apply') }}
                </button>
                <button class="btn btn-sm btn-secondary" @click="reset">
                    <i class="fa fa-fw fa-refresh"></i>&nbsp; {{ $t('cars.search.filter.reset') }}
                </button>
            </div>
        </b-collapse>
    </div>
</template>

<script>
    import VueSlider from 'vue-slider-component';

    export default {
        name: 'Filters',
        components: {
            VueSlider
        },
        props: ['packages'],
        data() {
            return {
                filterModel: {},
                availableFilters: {},
                lowestPrice: undefined,
                minPrice: undefined,
                highestPrice: undefined,
                maxPrice: undefined
            };
        },
        computed: {
            priceSliderValue() {
                return [
                    Math.min(Math.max(this.minPrice, this.lowestPrice), this.highestPrice),
                    Math.min(Math.max(this.maxPrice, this.lowestPrice), this.highestPrice)
                ];
            }
        },
        methods: {
            setup() {
                var filters = {
                    suppliers: {},
                    categories: {},
                    passengers: {},
                    baggage: {},
                    doors: {},
                    transmissionTypes: {},
                    driveTypes: {},
                    fuelTypes: {},
                    airConditioning: {},
                    rateQualifiers: {},
                    fuelPolicies: {},
                    mileage: {},
                    pickupLocation: {},
                    returnLocation: {}
                };
                for (let i = this.packages.length - 1; i >= 0; i--) {
                    let carPackage = this.packages[i];

                    if (typeof this.lowestPrice === 'undefined' || carPackage.totalPrice < this.lowestPrice) {
                        this.lowestPrice = carPackage.totalPrice;
                    }

                    if (typeof this.highestPrice === 'undefined' || carPackage.totalPrice > this.highestPrice) {
                        this.highestPrice = carPackage.totalPrice;
                    }

                    if (!filters.suppliers[carPackage.vendor.shortName]) {
                        filters.suppliers[carPackage.vendor.shortName] = {
                            type: 'image',
                            alt: carPackage.vendor.shortName,
                            src: carPackage.vendor.pictureUri,
                        };
                    }
                    if (!filters.categories[carPackage.vehicle.classCode]) {
                        filters.categories[carPackage.vehicle.classCode] = carPackage.vehicle.className;
                    }
                    if (!filters.passengers[carPackage.vehicle.passengerQuantity]) {
                        filters.passengers[carPackage.vehicle.passengerQuantity] = carPackage.vehicle.passengerQuantity;
                    }
                    if (!filters.baggage[carPackage.vehicle.baggageQuantity]) {
                        filters.baggage[carPackage.vehicle.baggageQuantity] = carPackage.vehicle.baggageQuantity;
                    }
                    if (!filters.doors[carPackage.vehicle.doorCount]) {
                        filters.doors[carPackage.vehicle.doorCount] = carPackage.vehicle.doorCount;
                    }
                    if (!filters.transmissionTypes[carPackage.vehicle.transmissionType]) {
                        filters.transmissionTypes[carPackage.vehicle.transmissionType] = carPackage.vehicle.transmissionTypeString;
                    }
                    if (!filters.driveTypes[carPackage.vehicle.driveType]) {
                        filters.driveTypes[carPackage.vehicle.driveType] = carPackage.vehicle.driveTypeString;
                    }
                    if (!filters.fuelTypes[carPackage.vehicle.fuelType]) {
                        filters.fuelTypes[carPackage.vehicle.fuelType] = carPackage.vehicle.fuelTypeString;
                    }
                    if (!filters.airConditioning[carPackage.vehicle.airConditioning]) {
                        filters.airConditioning[carPackage.vehicle.airConditioning] = carPackage.vehicle.airConditioning
                            ? 'yes'
                            : 'no';
                    }
                    if (!filters.rateQualifiers[carPackage.rateQualifierString]) {
                        filters.rateQualifiers[carPackage.rateQualifierString] = carPackage.rateQualifierString;
                    }
                    if (!filters.fuelPolicies[carPackage.fuelPolicyShortString]) {
                        filters.fuelPolicies[carPackage.fuelPolicyShortString] = carPackage.fuelPolicyShortString;
                    }
                    if (!filters.mileage[carPackage.mileageAllowanceString]) {
                        filters.mileage[carPackage.mileageAllowanceString] = carPackage.mileageAllowanceString;
                    }

                    if (!filters.pickupLocation[carPackage.pickupLocation.counterLocation]) {
                        filters.pickupLocation[carPackage.pickupLocation.counterLocation] = carPackage.pickupLocation.counterLocationString;
                    }

                    if (!filters.returnLocation[carPackage.returnLocation.counterLocation]) {
                        filters.returnLocation[carPackage.returnLocation.counterLocation] = carPackage.returnLocation.counterLocationString;
                    }
                }
                this.availableFilters = {};
                for (let filter in filters) {
                    this.availableFilters[filter] = {};
                    Object.keys(filters[filter]).sort().forEach((key) => {
                        this.availableFilters[filter][key] = filters[filter][key];
                    });
                }
                this.selectAll();
            },
            applyFilters() {
                const filteredPackages = [];
                for (let i = 0; i < this.packages.length; i++) {
                    let carPackage = this.packages[i];

                    if (Object.size(this.availableFilters.suppliers) > 1
                        && !this.filterModel.suppliers[carPackage.vendor.shortName]
                    ) {
                        continue;
                    }
                    if (Object.size(this.availableFilters.categories) > 1
                        && !this.filterModel.categories[carPackage.vehicle.classCode]
                    ) {
                        continue;
                    }
                    if (Object.size(this.availableFilters.passengers) > 1
                        && !this.filterModel.passengers[carPackage.vehicle.passengerQuantity]
                    ) {
                        continue;
                    }
                    if (Object.size(this.availableFilters.baggage) > 1
                        && !this.filterModel.baggage[carPackage.vehicle.baggageQuantity]
                    ) {
                        continue;
                    }
                    if (Object.size(this.availableFilters.doors) > 1
                        && !this.filterModel.doors[carPackage.vehicle.doorCount]
                    ) {
                        continue;
                    }
                    if (Object.size(this.availableFilters.transmissionTypes) > 1
                        && !this.filterModel.transmissionTypes[carPackage.vehicle.transmissionType]
                    ) {
                        continue;
                    }
                    if (Object.size(this.availableFilters.driveTypes) > 1
                        && !this.filterModel.driveTypes[carPackage.vehicle.driveType]
                    ) {
                        continue;
                    }
                    if (Object.size(this.availableFilters.fuelTypes) > 1
                        && !this.filterModel.fuelTypes[carPackage.vehicle.fuelType]
                    ) {
                        continue;
                    }
                    if (Object.size(this.availableFilters.airConditioning) > 1
                        && !this.filterModel.airConditioning[carPackage.vehicle.airConditioning]
                    ) {
                        continue;
                    }
                    if (Object.size(this.availableFilters.rateQualifiers) > 1
                        && !this.filterModel.rateQualifiers[carPackage.rateQualifierString]
                    ) {
                        continue;
                    }
                    if (Object.size(this.availableFilters.fuelPolicies) > 1
                        && !this.filterModel.fuelPolicies[carPackage.fuelPolicyShortString]
                    ) {
                        continue;
                    }
                    if (Object.size(this.availableFilters.mileage) > 1
                        && !this.filterModel.mileage[carPackage.mileageAllowanceString]
                    ) {
                        continue;
                    }
                    if (Object.size(this.availableFilters.pickupLocation) > 1
                        && !this.filterModel.pickupLocation[carPackage.pickupLocation.counterLocation]
                    ) {
                        continue;
                    }
                    if (Object.size(this.availableFilters.returnLocation) > 1
                        && !this.filterModel.returnLocation[carPackage.returnLocation.counterLocation]
                    ) {
                        continue;
                    }
                    if (carPackage.totalPrice < this.minPrice) {
                        continue;
                    }

                    if (carPackage.totalPrice > this.maxPrice) {
                        continue;
                    }
                    filteredPackages.push(carPackage);
                }
                this.$emit('filters-applied', filteredPackages);
            },
            handlePriceChange(value) {
                this.minPrice = value[0];
                this.maxPrice = value[1];
            },
            refreshSlider() {
                this.$nextTick(() => this.$refs.priceSlider.refresh());
            },
            selectAll() {
                let filters = {};
                for (let filter in this.availableFilters) {
                    filters[filter] = {};
                    for (let key in this.availableFilters[filter]) {
                        filters[filter][key] = 1;
                    }
                }
                this.filterModel = filters;
                this.minPrice = this.lowestPrice;
                this.maxPrice = this.highestPrice;
            },
            reset() {
                this.selectAll();
                this.applyFilters();
            },
        },
        watch: {
            packages() {
                this.setup();
            },
        },
        mounted() {
            this.setup();
        }
    };
</script>

<style scoped>

</style>